import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Space,
  Table,
} from "antd";
import { useEffect, useState } from "react";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  disabled,
  children,
  ...restProps
}) => {
  const inputNode = (() => {
    if (inputType === "number") {
      if (dataIndex === "sequence") {
        return (
          <InputNumber
            min={1}
            precision={0} // no decimal places
            style={{ width: "100%" }}
            disabled={disabled}
          />
        );
      } else {
        return (
          <InputNumber
            min={0}
            step={0.01}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        );
      }
    }
    return <Input disabled={disabled} />;
  })();
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[{ required: true, message: `Please Input ${title}!` }]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export default function ItemDetailsCard({
  selectedCategory,
  onClearSelection,
  manageCategoryItem,
  getCategoryConfig,
  editingId,
  setEditingId,
}) {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(selectedCategory?.items || []);
    form.resetFields();
  }, [selectedCategory, form]);

  const isEditing = (record) => record.id === editingId;

  const edit = (record) => {
    form.setFieldsValue(record);
    setEditingId(record.id);
  };

  const cancel = () => {
    const itemToCancel = items.find((item) => item.id === editingId);
    if (itemToCancel?.isNew) {
      setItems(items.filter((item) => item.id !== editingId));
    }
    setEditingId("");
  };

  const save = async (id) => {
    try {
      const rowData = await form.validateFields();
      const itemToSave = items.find((item) => item.id === id);

      const handleSuccess = () => {
        message.success("Item saved successfully");
        getCategoryConfig(true);
      };

      // Differentiate between new item and existing item
      if (itemToSave?.isNew) {
        manageCategoryItem({
          method: "POST",
          body: {
            ...rowData,
            parent_id: selectedCategory.id,
            table: "Category",
          },
          onSuccessCallback: handleSuccess,
        });
      } else {
        manageCategoryItem({
          method: "PUT",
          body: { ...rowData, id, table: "Category" },
          onSuccessCallback: handleSuccess,
        });
      }
      setEditingId("");
    } catch (error) {
      console.log("Validate Failed:", error);
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this item?",
      icon: <ExclamationCircleFilled />,
      content: "This action cannot be undone.",
      onOk() {
        manageCategoryItem({
          method: "DELETE",
          body: { id, table: "Category" },
          onSuccessCallback: () => {
            message.success("Item deleted successfully");
            getCategoryConfig(true);
          },
        });
      },
      onCancel() {
        console.log("Delete canceled");
      },
    });
  };

  const handleAddItem = () => {
    if (editingId) return;
    const newItem = {
      id: `new_${Date.now()}`, // temporary unique ID
      name: "New Item",
      price: 0,
      sequence: 0,
      isNew: true, // flag to identify new items
    };
    setItems([...items, newItem]);
    edit(newItem);
  };

  const itemColumns = [
    { title: "Item Name", dataIndex: "name", editable: true },
    {
      title: "Price",
      dataIndex: "price",
      render: (text) => `RM ${(text || 0).toFixed(2)}`,
      editable: true,
    },
    {
      title: "Sequence",
      dataIndex: "sequence",
      editable: true,
    },
    {
      title: "Action",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button type="primary" onClick={() => save(record.id)} size="small">
              Save
            </Button>
            <Button onClick={cancel} size="small">
              Cancel
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              icon={<EditOutlined />}
              type="primary"
              size="small"
              onClick={() => edit(record)}
              disabled={!!editingId}
            >
              Edit
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              onClick={() => handleDelete(record.id)}
              disabled={!!editingId}
            >
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = itemColumns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: ["price", "sequence"].includes(col.dataIndex)
          ? "number"
          : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        disabled: col.dataIndex === "sequence" && record.isNew, // Disable sequence for new items
      }),
    };
  });

  return (
    <Card
      title={
        selectedCategory
          ? `Managing: ${selectedCategory.name}`
          : "Items Details"
      }
      style={{ height: "100%" }}
      extra={
        selectedCategory && (
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddItem}
              disabled={!!editingId}
            >
              Add Item
            </Button>
            <Button
              type="primary"
              danger
              onClick={onClearSelection}
              disabled={!!editingId}
            >
              Close
            </Button>
          </Space>
        )
      }
    >
      <Form form={form} component={false}>
        {selectedCategory ? (
          <Table
            rowKey="id"
            components={{ body: { cell: EditableCell } }}
            columns={mergedColumns}
            dataSource={items}
            pagination={false}
            scroll={{ y: "calc(100vh - 280px)" }}
          />
        ) : (
          <Empty
            description="Select a category to manage its items"
            style={{ paddingTop: "20vh" }}
          />
        )}
      </Form>
    </Card>
  );
}
